import React from 'react';
import {
  Badge, Box, Divider, HStack, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Stack, Table, Tbody, Td, Text, Th, Thead, Tr, Button, useBreakpointValue, useColorModeValue,
} from '@chakra-ui/react';
import { useHistory } from 'react-router-dom';
import { KpiBreakdown } from '../../hooks/useDashboardMetrics';
import { money } from '../../utils/money';

// item 1481 (Chad: "a break down modal if I click a KPI where it will show me how the numbers
// are derived") — the rule in plain English, the arithmetic line by line, then every row that
// made the number (click a row to open the document). Counts render as plain numbers.
const KpiBreakdownModal: React.FC<{ breakdown: KpiBreakdown | null; onClose: () => void; isCount?: boolean }> = ({ breakdown: b, onClose, isCount }) => {
  const history = useHistory();
  const sub = useColorModeValue('gray.600', 'whiteAlpha.700');
  const fmt = (n: number) => (isCount ? String(n) : money(n));
  // item 1508 (Chad, iPhone screenshots) — the breakdown (three-column tables) is full-screen
  // below md (Chakra 1.6.7's Modal takes no responsive size object, hence the hook); 3xl at md+
  const modalSize = useBreakpointValue({ base: 'full', md: '3xl' }) ?? '3xl';
  return (
    <Modal isOpen={!!b} onClose={onClose} size={modalSize} scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {b?.title}
          {b && <Badge ml={3} fontSize="0.8rem">{fmt(b.total)}</Badge>}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {b && (
            <Stack spacing={5}>
              <Box>
                <Text fontSize="xs" fontWeight="bold" letterSpacing="0.1em" color={sub} mb={1}>HOW IT IS DERIVED</Text>
                {b.rule.map((r) => <Text key={r} fontSize="sm">{`• ${r}`}</Text>)}
              </Box>
              <Box>
                <Text fontSize="xs" fontWeight="bold" letterSpacing="0.1em" color={sub} mb={1}>THE ARITHMETIC</Text>
                {b.arithmetic.map((a, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <HStack key={`${a.label}-${i}`} justify="space-between" fontSize="sm" fontWeight={a.op === '=' ? 'bold' : 'normal'} borderTopWidth={a.op === '=' ? '1px' : 0} pt={a.op === '=' ? 1 : 0}>
                    <Text>{`${a.op && a.op !== '=' ? `${a.op} ` : ''}${a.label}`}</Text>
                    <Text fontFamily="mono">{fmt(a.amount)}</Text>
                  </HStack>
                ))}
              </Box>
              {b.sections.filter((s) => s.rows.length > 0).map((s) => (
                <Box key={s.title}>
                  <Divider mb={2} />
                  <HStack justify="space-between" mb={1}>
                    <Text fontSize="xs" fontWeight="bold" letterSpacing="0.1em" color={sub}>{`${s.title.toUpperCase()} (${s.rows.length})`}</Text>
                    <Text fontSize="sm" fontWeight="bold" fontFamily="mono">{fmt(s.total)}</Text>
                  </HStack>
                  {s.note && <Text fontSize="xs" color={sub} mb={1}>{s.note}</Text>}
                  {/* item 1508 — the rows table scrolls inside its own box on a phone (plain Box, not
                      TableContainer: that one forces white-space nowrap on every cell) */}
                  <Box overflowX="auto">
                    <Table size="sm" variant="simple">
                      <Thead>
                        <Tr>
                          <Th>Document</Th>
                          <Th>Detail</Th>
                          <Th isNumeric>Amount</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {s.rows.map((r, i) => (
                          // eslint-disable-next-line react/no-array-index-key
                          <Tr key={`${r.label}-${i}`} cursor={r.href ? 'pointer' : undefined} _hover={r.href ? { opacity: 0.8 } : undefined} onClick={() => { if (r.href) { onClose(); history.push(r.href); } }}>
                            <Td fontFamily="mono" whiteSpace="nowrap">{r.label}</Td>
                            <Td><Text fontSize="xs" color={sub} noOfLines={1}>{r.sub}</Text></Td>
                            <Td isNumeric fontFamily="mono">{fmt(r.amount)}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                </Box>
              ))}
            </Stack>
          )}
        </ModalBody>
        <ModalFooter><Button onClick={onClose}>Close</Button></ModalFooter>
      </ModalContent>
    </Modal>
  );
};

KpiBreakdownModal.defaultProps = { isCount: false };

export default KpiBreakdownModal;
